import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Nav-variant logo (Feb 2026 corrected performance pass).
//
// The previous iteration replaced the CMS-uploaded wordmark with an
// inline SVG "UD + Ur Designs." lockup. That changed the visual
// appearance — the customer expects the exact original Ur Designs
// wordmark that was uploaded to the CMS.
//
// Fix: render the actual CMS-uploaded logo <img> directly, using the
// current known-good WebP URL as the DEFAULT initial src so the
// browser can request it on first render WITHOUT waiting for
// /api/settings to resolve. If /api/settings later returns a
// different site_logo (admin has since re-uploaded via the
// dashboard), the src is updated in place — no opacity animation,
// no cross-fade, no placeholder swap.
//
// Explicit height reserves layout so a re-render never shifts nav
// rhythm. loading=eager, fetchpriority=high, decoding=async keep
// the request at the front of the queue.

// Canonical optimized logo asset. Served from the frontend's static
// `public/brand/` folder (Feb 2026 mobile-latency audit) so it does
// NOT go through the FastAPI `/api/uploads/` route. Same-origin
// static delivery removes the intermittent 200-4000 ms backend-TTFB
// variance seen when this 3.4 KB file was served through Python.
// `settings.site_logo` still overrides at runtime if an admin
// uploads a replacement via the CMS dashboard.
const DEFAULT_NAV_LOGO = "/brand/ur-designs-logo.webp";

// Intrinsic dimensions of the canonical Ur Designs wordmark asset.
// Used to compute the exact width at any target height so the
// browser reserves the correct box on first paint (no oversized-then-
// snap flash). If an admin uploads a different-aspect logo via the
// CMS, we lazily switch to the loaded image's real aspect ratio via
// the onLoad callback below.
const LOGO_INTRINSIC_WIDTH = 409;
const LOGO_INTRINSIC_HEIGHT = 74;
const LOGO_ASPECT_RATIO = LOGO_INTRINSIC_WIDTH / LOGO_INTRINSIC_HEIGHT;

// Default heights that MATCH the current CMS values
// (logo_height_nav=29, logo_height_footer=44). Previously the nav
// default was 44 which caused the mobile logo to render at ~172-244
// px wide before /settings resolved to 31, then visibly snap smaller.
// Keeping defaults in sync with the live CMS values eliminates the
// snap on every cold load.
const DEFAULT_LOGO_HEIGHT_NAV = 29;
const DEFAULT_LOGO_HEIGHT_FOOTER = 44;

export const Logo = ({ variant = "nav", size }) => {
  const isLg = size === "lg";

  // Initial src: canonical logo for nav/admin variants renders
  // instantly. Footer starts empty and hydrates from CMS.
  const [logo, setLogo] = useState(
    variant === "footer" ? "" : DEFAULT_NAV_LOGO,
  );
  const [heights, setHeights] = useState({
    nav: DEFAULT_LOGO_HEIGHT_NAV,
    footer: DEFAULT_LOGO_HEIGHT_FOOTER,
  });
  // Tracks the loaded image's real aspect ratio, used only when the
  // admin uploads a wordmark whose aspect differs from the current
  // Ur Designs mark. Defaults to the intrinsic value so first-paint
  // width is correct.
  const [aspectRatio, setAspectRatio] = useState(LOGO_ASPECT_RATIO);

  useEffect(() => {
    api.get("/settings").then((r) => {
      if (r?.data?.site_logo) setLogo(r.data.site_logo);
      setHeights({
        nav: Number(r.data.logo_height_nav) || DEFAULT_LOGO_HEIGHT_NAV,
        footer: Number(r.data.logo_height_footer) || DEFAULT_LOGO_HEIGHT_FOOTER,
      });
    }).catch(() => {});
  }, []);

  const pxHeight = isLg ? 64 : (variant === "footer" ? heights.footer : heights.nav);
  const pxWidth = Math.round(pxHeight * aspectRatio);

  if (logo) {
    return (
      <img
        src={logo}
        alt="Ur Designs"
        data-testid="site-logo-img"
        // Both dimensions set explicitly so the browser reserves the
        // exact display box on first paint — before CSS hydration
        // and before image decode. Prevents the "logo renders at
        // intrinsic 409×74 then snaps to 172×31" flash we saw on
        // mobile cold loads.
        width={pxWidth}
        height={pxHeight}
        style={{ height: `${pxHeight}px`, width: `${pxWidth}px` }}
        className="object-contain"
        // Above-the-fold nav logo — eager + high priority so it is
        // fetched at the front of the queue and paints on first
        // frame. No lazy loading, no opacity animation.
        loading={variant === "footer" ? "lazy" : "eager"}
        fetchPriority={variant === "footer" ? "auto" : "high"}
        decoding="async"
        onLoad={(e) => {
          // If the actual loaded image has a different aspect ratio
          // than our intrinsic constant (admin uploaded a
          // different-shape wordmark), recompute width so it
          // stays proportional. Same-aspect uploads produce no diff.
          const img = e.currentTarget;
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            const ratio = img.naturalWidth / img.naturalHeight;
            if (Math.abs(ratio - aspectRatio) > 0.01) setAspectRatio(ratio);
          }
        }}
      />
    );
  }

  // Fallback wordmark — only rendered when no `logo` URL is set at
  // all (e.g. brand-new install with a blank site_logo). Keeps the
  // header from being empty in that edge case.
  const scale = pxHeight / 44;
  return (
    <div style={{ height: `${pxHeight}px` }} className="flex items-center">
      <div
        data-testid="site-logo-fallback"
        style={{ transform: `scale(${scale})`, transformOrigin: "left center" }}
        className="flex items-center gap-3"
      >
        <div className={`relative ${isLg ? "w-16 h-16" : "w-12 h-12"} shrink-0`}>
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FF6600] to-[#FF9D40] blur-[8px] opacity-40" />
          <div className="absolute inset-0 grid place-items-center font-display font-black text-white">
            <svg viewBox="0 0 64 64" className="w-full h-full">
              <defs>
                <linearGradient id="urg" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FFFFFF" />
                  <stop offset="100%" stopColor="#A3A3A3" />
                </linearGradient>
              </defs>
              <text x="50%" y="58%" textAnchor="middle"
                fontFamily="Satoshi, Outfit, sans-serif" fontSize="34" fontWeight="900"
                fill="url(#urg)" letterSpacing="-2">UD</text>
              <circle cx="49" cy="46" r="5" fill="#FF6600" />
            </svg>
          </div>
        </div>
        <div className="leading-none min-w-0">
          <div className={`font-display font-extrabold tracking-tight text-white ${isLg ? "text-3xl" : "text-xl"}`}>
            Ur Designs<span className="text-[#FF6600]">.</span>
          </div>
          <div className="hidden sm:block text-[9px] tracking-[0.25em] text-[#A3A3A3] uppercase mt-1 whitespace-nowrap">
            Branding · Motion · 3D Renders
          </div>
        </div>
      </div>
    </div>
  );
};
